// src/pages/client-tabs/ClientInfoTab.jsx
import React, { memo } from 'react';
import {
  Mail,
  Phone,
  Globe,
  MapPin,
  Building2,
  Users,
  User as UserIcon,
  Copy,
  Info,
  MessageSquare,
  Tag,
} from 'lucide-react';

/* -------------------------------- helpers -------------------------------- */

const labelClass = 'text-[11px] font-semibold tracking-wide text-gray-500';
const valueClass = 'text-[15px] leading-6 font-medium text-gray-900 truncate';
const grid2 = 'grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4';
const grid3 = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4';

function formatAddress(address) {
  if (!address) return '';
  if (typeof address === 'string') return address;
  const { street, city, state, zip, country, raw } = address || {};
  const structured = [street, city, state, zip, country].filter(Boolean).join(', ');
  return structured || raw || '';
}
function formatUser(user) {
  if (!user) return '';
  if (typeof user === 'string') return user;
  return user.name || user.email || '';
}
function formatDate(dt) {
  if (!dt) return '';
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}
function ensureHttp(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}
function copyToClipboard(text) {
  if (!text) return;
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
  }
}

/* ------------------------------ UI atoms --------------------------------- */

const Field = ({ icon: Icon, label, value, href, copyable }) => {
  const show = value != null && String(value).trim() !== '';
  const display = show ? String(value) : '—';
  const isLink = !!(href && show);

  return (
    <div className="min-w-0">
      <div className="flex items-start gap-2">
        {Icon ? <span className="mt-0.5 text-gray-500"><Icon size={16} /></span> : null}
        <div className="min-w-0">
          <div className={labelClass}>{label}</div>
          <div className="flex items-center gap-2 min-w-0">
            {isLink ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={`${valueClass} hover:underline`}
                title={display}
              >
                {display}
              </a>
            ) : (
              <div className={valueClass} title={display}>{display}</div>
            )}
            {copyable && show ? (
              <button
                type="button"
                onClick={() => copyToClipboard(display)}
                className="inline-flex items-center rounded-md p-1 hover:bg-gray-100 text-gray-500"
                title="Copy"
                aria-label={`Copy ${label.toLowerCase()}`}
              >
                <Copy size={14} />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const PlainField = ({ label, value }) => {
  const show = value != null && String(value).trim() !== '';
  const display = show ? String(value) : '—';
  return (
    <div className="min-w-0">
      <div className={labelClass}>{label}</div>
      <div className={valueClass} title={display}>{display}</div>
    </div>
  );
};

const GroupTitle = ({ children }) => (
  <div className="flex items-center justify-between">
    <div className="text-[11px] font-bold tracking-wider uppercase text-gray-500">{children}</div>
  </div>
);

const Divider = () => <div className="h-px bg-gray-100 my-5" />;

/* ------------------------------- main ------------------------------------ */
/**
 * Premium single-card layout:
 * - Always shows all key fields; empty displays '—'
 * - Notes composer + list inside (reads note.text, falls back to note)
 * - Quick Links / Meta in page sidebar (ClientDetail)
 */
function ClientInfoTab({
  client,
  canEdit,
  onEdit,
  // Notes props (state owned by ClientDetail)
  notes = [],
  noteDraft = '',
  onChangeNote,
  onAddNote,
  savingNote = false,
}) {
  if (!client) {
    return (
      <div className="h-40 flex items-center justify-center text-gray-400 text-base">
        Loading client...
      </div>
    );
  }

  // Derived values
  const addressStr = formatAddress(client.address);
  const mapsHref = addressStr
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressStr)}`
    : '';
  const website = client.website ? ensureHttp(client.website) : '';
  const statusLabel = client.status
    ? client.status.charAt(0).toUpperCase() + client.status.slice(1)
    : '';

  // Notes list (newest first), tolerant to {text} or {note}
  const sortedNotes = Array.isArray(notes) && notes.length
    ? [...notes].sort((a, b) => +new Date(b.createdAt || b.at || 0) - +new Date(a.createdAt || a.at || 0))
    : [];

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
      {/* ---- Header ---- */}
      <div className="flex items-center justify-between px-5 pt-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              {client.name || 'Client'}
            </h2>
            {statusLabel ? (
              <span className="rounded-full bg-gray-100 text-gray-700 text-xs px-2 py-0.5">
                {statusLabel}
              </span>
            ) : null}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">Client Profile</div>
        </div>
        {canEdit ? (
          <button
            onClick={onEdit}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            Edit
          </button>
        ) : null}
      </div>

      {/* ---- Body ---- */}
      <div className="px-5 pb-6 pt-4">
        {/* Business */}
        <GroupTitle>Business</GroupTitle>
        <div className={`mt-3 ${grid3}`}>
          <Field icon={Building2} label="Company" value={client.company} />
          <Field icon={UserIcon} label="Contact Person" value={client.contactPerson} />
          <Field icon={Users} label="Assigned To" value={formatUser(client.assignedTo)} />
          {/* ALIGNMENT FIX: use Field with icons for these three so all columns share the same gutter */}
          <Field icon={Info} label="Status" value={statusLabel} />
          <Field icon={MessageSquare} label="Preferred Contact" value={client.preferredContactMethod} />
          <Field icon={Tag} label="Industry" value={client.industry} />
        </div>

        <Divider />

        {/* Contact */}
        <GroupTitle>Contact</GroupTitle>
        <div className={`mt-3 ${grid2}`}>
          <Field
            icon={Mail}
            label="Email"
            value={client.email}
            href={client.email ? `mailto:${client.email}` : ''}
            copyable
          />
          <Field
            icon={Phone}
            label="Phone"
            value={client.phone}
            href={client.phone ? `tel:${client.phone}` : ''}
            copyable
          />
          <Field
            icon={Globe}
            label="Website"
            value={client.website}
            href={website}
          />
          <Field
            icon={MapPin}
            label="Address"
            value={addressStr}
            href={mapsHref}
            copyable
          />
          <Field
            icon={Mail}
            label="Secondary Email"
            value={client.secondaryEmail}
            href={client.secondaryEmail ? `mailto:${client.secondaryEmail}` : ''}
            copyable
          />
          <Field
            icon={Phone}
            label="Secondary Phone"
            value={client.secondaryPhone}
            href={client.secondaryPhone ? `tel:${client.secondaryPhone}` : ''}
            copyable
          />
        </div>

        <Divider />

        {/* Lifecycle / CRM */}
        <GroupTitle>Lifecycle</GroupTitle>
        <div className={`mt-3 ${grid3}`}>
          <PlainField label="Source" value={client.source} />
          <PlainField label="Last Interaction" value={formatDate(client.lastInteractionAt)} />
          <PlainField label="Last Contacted By" value={formatUser(client.lastContactedBy)} />
        </div>

        {/* Custom Fields */}
        {client.customFields && Object.keys(client.customFields).length > 0 ? (
          <>
            <Divider />
            <GroupTitle>Custom Fields</GroupTitle>
            <div className={`mt-3 ${grid3}`}>
              {Object.entries(client.customFields).map(([key, val]) => {
                const label = key
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/_/g, ' ')
                  .replace(/\s+/g, ' ')
                  .trim();
                const display =
                  val === null || typeof val === 'undefined' || String(val).trim() === ''
                    ? '—'
                    : String(val);
                return <PlainField key={key} label={label} value={display} />;
              })}
            </div>
          </>
        ) : null}

        {/* Notes */}
        <Divider />
        <GroupTitle>Notes</GroupTitle>
        <div className="mt-3 space-y-3">
          <textarea
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
            placeholder="Add a quick note…"
            value={noteDraft}
            onChange={onChangeNote}
          />
          <div className="flex justify-end">
            <button
              disabled={savingNote}
              onClick={onAddNote}
              className={`inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold ${
                savingNote ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {savingNote ? 'Saving…' : 'Save Note'}
            </button>
          </div>

          {sortedNotes.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {sortedNotes.map((n, idx) => {
                const who = formatUser(n.createdBy) || '—';
                const when = formatDate(n.createdAt || n.at) || '—';
                const body =
                  typeof n === 'string'
                    ? n
                    : (n.text ?? n.note ?? '');
                return (
                  <li key={n._id || n.id || idx} className="py-2 text-sm">
                    <div className="text-gray-900 whitespace-pre-wrap">{(body && body.trim()) ? body : '—'}</div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400">
                      <span>{when}</span>
                      {who ? <span>• {who}</span> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-xs text-gray-500">No notes yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(ClientInfoTab);
